import type { OperatorRigState, Point3 } from "./operatorRig";

/** Rigid local-to-model joint transform, shared by art and authoritative damage geometry. */
export type RigTransform = readonly [number, number, number, number, number, number, number, number, number, number, number, number];
export const RIG_IDENTITY: RigTransform = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0];

export function jointTransform(pivot: Point3, yaw: number, pitch: number, roll: number, offset: Point3): RigTransform {
    const c = Math.cos(yaw), s = Math.sin(yaw), cp = Math.cos(pitch), sp = Math.sin(pitch), cr = Math.cos(roll), sr = Math.sin(roll);
    const a = c * cp, b = c * sp * sr - s * cr, d = c * sp * cr + s * sr;
    const e = s * cp, f = s * sp * sr + c * cr, g = s * sp * cr - c * sr;
    const h = -sp, i = cp * sr, j = cp * cr;
    return [a, b, d, pivot.x + offset.x - a * pivot.x - b * pivot.y - d * pivot.z,
        e, f, g, pivot.y + offset.y - e * pivot.x - f * pivot.y - g * pivot.z,
        h, i, j, pivot.z + offset.z - h * pivot.x - i * pivot.y - j * pivot.z];
}

export function transformRigPoint(t: RigTransform, p: Point3, normal = false): Point3 {
    return { x: t[0] * p.x + t[1] * p.y + t[2] * p.z + (normal ? 0 : t[3]),
        y: t[4] * p.x + t[5] * p.y + t[6] * p.z + (normal ? 0 : t[7]),
        z: t[8] * p.x + t[9] * p.y + t[10] * p.z + (normal ? 0 : t[11]) };
}

/** Compose the joint with the camera once per body group, not once per vertex. */
export function rigProjection(cy: number, sy: number, t: RigTransform = RIG_IDENTITY): RigTransform {
    const row = (a: number, b: number, c: number, offset: number): number[] => [
        a * t[0] + b * t[4] + c * t[8], a * t[1] + b * t[5] + c * t[9],
        a * t[2] + b * t[6] + c * t[10], a * t[3] + b * t[7] + c * t[11] + offset
    ];
    return [...row(cy * 1.15, -sy * 1.15, 0, 0), ...row(sy * 0.54, cy * 0.54, -0.84, 2.55),
        ...row(sy * 0.84, cy * 0.84, 0.54, 0)] as unknown as RigTransform;
}

export function rigLight(cy: number, sy: number, t: RigTransform): Point3 {
    const x = -cy * 0.45 - sy * 0.3, y = sy * 0.45 - cy * 0.3;
    return { x: x * t[0] + y * t[4] + 0.83 * t[8], y: x * t[1] + y * t[5] + 0.83 * t[9],
        z: x * t[2] + y * t[6] + 0.83 * t[10] };
}

export function interpolateOperatorAngle(from: number, to: number, t: number): number {
    return from + Math.atan2(Math.sin(to - from), Math.cos(to - from)) * t;
}

export const OPERATOR_STANCE = 0.58;
export function operatorFoot(state: OperatorRigState, side: number): { center: Point3; lift: number; planted: boolean; yaw: number; pitch: number } {
    const yaw = Math.atan2(Math.sin(state.rotation) / 0.54, Math.cos(state.rotation));
    const travel = Math.atan2(Math.sin(state.moveAngle) / 0.54, Math.cos(state.moveAngle)) - yaw;
    const t = ((state.phase + (side < 0 ? 0.5 : 0)) % 1 + 1) % 1;
    const swing = Math.max(0, (t - OPERATOR_STANCE) / (1 - OPERATOR_STANCE));
    // Linear stance and an eased return with a higher mid-swing toe clearance.
    const step = t < OPERATOR_STANCE ? 1 - 2 * t / OPERATOR_STANCE : -Math.cos(swing * Math.PI);
    const lift = Math.sin(swing * Math.PI) ** 2 * state.moving;
    const pitch = (t < OPERATOR_STANCE ? Math.sin(t / OPERATOR_STANCE * Math.PI * 2) * 0.12 : -Math.sin(swing * Math.PI * 2) * 0.25) * state.moving;
    return { center: { x: 0.08 + Math.cos(travel) * step * 1.08 * state.moving,
        y: side * 0.56 + Math.sin(travel) * step * 1.08 * state.moving, z: 0.24 + lift * 0.48 + Math.abs(Math.sin(pitch)) * 0.60 },
    lift, planted: state.moving < 0.08 || t < OPERATOR_STANCE,
    // Backpedalling keeps the toes toward the target; strafing opens the stance.
    yaw: Math.sin(travel) * 0.55 * state.moving,
    pitch: pitch || 0 };
}

/** Analytic two-bone joint. The pole sets the anatomical bend plane. */
export function solveOperatorJoint(root: Point3, target: Point3, pole: Point3, upper: number, lower: number, reachRatio = 0.999): Point3 {
    const dx = target.x - root.x, dy = target.y - root.y, dz = target.z - root.z;
    const distance = Math.max(1e-6, Math.hypot(dx, dy, dz)), x = dx / distance, y = dy / distance, z = dz / distance;
    // Long weapon grips may exceed the neutral arm reach. Extend both links evenly,
    // keeping the hand exactly on its gameplay grip instead of pulling it off the gun.
    const extension = Math.max(1, distance / ((upper + lower) * reachRatio));
    upper *= extension; lower *= extension;
    const along = Math.max(0, Math.min(distance, (upper * upper - lower * lower + distance * distance) / (2 * distance)));
    const bend = Math.sqrt(Math.max(0, upper * upper - along * along));
    const dot = pole.x * x + pole.y * y + pole.z * z;
    let px = pole.x - x * dot, py = pole.y - y * dot, pz = pole.z - z * dot;
    let length = Math.hypot(px, py, pz);
    if (length < 1e-6) { px = -y; py = x; pz = 0; length = Math.hypot(px, py) || 1; }
    return { x: root.x + x * along + px / length * bend, y: root.y + y * along + py / length * bend,
        z: root.z + z * along + pz / length * bend };
}

export function operatorBodyPose(state: OperatorRigState): { bounce: number; hips: RigTransform; chest: RigTransform; head: RigTransform } {
    const cycle = state.phase * Math.PI * 2, weight = state.moving;
    const yaw = Math.atan2(Math.sin(state.rotation) / 0.54, Math.cos(state.rotation));
    const travel = Math.atan2(Math.sin(state.moveAngle) / 0.54, Math.cos(state.moveAngle)) - yaw;
    const beat = Math.sin(cycle) * weight, bounce = (1 - Math.cos(cycle * 2)) * weight * 0.065;
    const dx = Math.cos(travel), dy = Math.sin(travel);
    const pivot = { x: 0, y: 0, z: 2.35 + bounce };
    const offset = { x: -dy * beat * 0.085, y: dx * beat * 0.085, z: 0 };
    const hips = jointTransform(pivot, beat * 0.10, dx * weight * 0.035, -dy * weight * 0.035, offset);
    const chest = jointTransform(pivot, -beat * 0.085, dx * weight * 0.12, -dy * weight * 0.12 + beat * 0.025, offset);
    // The neck follows the torso, but the eyes counter-rotate to keep aim readable.
    const neck = { x: 0.04, y: 0, z: 3.97 + bounce }, attached = transformRigPoint(chest, neck);
    const head = jointTransform(neck, -beat * 0.02, dx * weight * 0.025, -dy * weight * 0.025,
        { x: attached.x - neck.x, y: attached.y - neck.y, z: attached.z - neck.z });
    return { bounce, hips, chest, head };
}
